import { useNavigate } from "react-router-dom";
import PublicNavbar from "@/components/layout/PublicNavbar";
import ChatBubble from "@/components/ChatBubble";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { motion } from "framer-motion";

const fadeUp = { hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0 } };
const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/6d089f85-473b-4449-a6e3-5eb88daae93b/images/f216edd259c11b8b8681ac867e3215e8a7e5ab45f0cc3b5cd0736e2f35a844b0.jpeg";

export default function HomePage() {
  const navigate = useNavigate();

  return (
    <div className="h-screen overflow-hidden relative" data-testid="home-page">
      <PublicNavbar />

      {/* Full-screen Hero — single viewport */}
      <section id="hero" className="relative h-screen" data-testid="hero-section">
        {/* Sky blue diagonal */}
        <div className="absolute inset-0 bg-sky-500" style={{ clipPath: "polygon(0 0, 65% 0, 45% 100%, 0 100%)" }} />
        {/* Amber diagonal */}
        <div className="absolute inset-0 bg-amber-500" style={{ clipPath: "polygon(65% 0, 100% 0, 100% 100%, 45% 100%)" }} />
        {/* Hero image overlay */}
        <div className="absolute right-0 top-0 w-[55%] h-full hidden md:block">
          <img src={HERO_IMG} alt="Happy students at Legacy Heights" className="w-full h-full object-cover mix-blend-multiply opacity-60" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center h-full">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={{ visible: { transition: { staggerChildren: 0.15 } } }}
            className="max-w-xl"
          >
            <motion.p variants={fadeUp} className="text-slate-900 font-heading font-bold text-sm uppercase tracking-[0.25em] mb-4">
              Student Accommodation
            </motion.p>
            <motion.h1 variants={fadeUp} className="font-heading text-5xl sm:text-6xl lg:text-7xl font-black text-slate-900 tracking-tighter leading-[0.95] mb-6">
              It's all
              <br />
              right here
            </motion.h1>
            <motion.div variants={fadeUp} className="bg-amber-500 text-slate-900 px-6 py-4 mb-3 inline-block">
              <p className="text-base font-semibold leading-snug">
                Your one-stop platform for student accommodation: browse rooms, apply, sign leases, and manage your stay.
              </p>
            </motion.div>
            <motion.div variants={fadeUp} className="bg-slate-900 text-white px-6 py-3 mb-8 inline-block w-full">
              <p className="text-sm">
                Premium student living at Legacy Heights, Harare. Explore our rooms, join the community, or chat with us below.
              </p>
            </motion.div>
            <motion.div variants={fadeUp} className="flex flex-wrap gap-4">
              <Button
                size="lg"
                className="bg-slate-900 hover:bg-slate-800 text-white rounded-none h-13 px-8 text-base font-bold"
                onClick={() => navigate("/rooms")}
                data-testid="hero-browse-btn"
              >
                Browse Rooms <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white rounded-none h-13 px-8 text-base font-bold"
                onClick={() => navigate("/register")}
                data-testid="hero-apply-btn"
              >
                Apply Now
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <ChatBubble botType="public" title="Legacy Heights Assistant" subtitle="Ask about accommodation" accentColor="sky" />
    </div>
  );
}
